import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import z from "zod";

import { Column } from "@/components";
import { ErrorText } from "@/components/ErrorText";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobStatus } from "@/types";

const formSchema = z.object({
  companyId: z.string().min(1, "Required"),
  jobId: z.string().min(1, "Required"),
});

type FormSchema = z.infer<typeof formSchema>;

export const JobSelectionDialog = ({
  handleSubmit,
  isError: isSubmitError,
  isPending: isSubmitPending,
  onOpenChange,
  open,
}: {
  handleSubmit: (jobId: string, companyId?: string) => void;
  isError: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  const companies = useGetCachedCompaniesSummary();
  const companiesWithJobs = companies.filter(({ jobs }) => jobs.length > 0);

  const form = useForm<FormSchema>({
    defaultValues: {
      companyId: "",
      jobId: "",
    },
    resolver: zodResolver(formSchema),
  });

  const companyId = useWatch<FormSchema>({
    control: form.control,
    name: "companyId",
  });

  const company = companiesWithJobs.find(({ id }) => id === companyId);
  const jobs =
    company?.jobs.filter((job) => job.status !== JobStatus.CLOSED) ?? [];

  const companyOptions = companiesWithJobs.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  const jobOptions = jobs.map((job) => ({
    value: job.id,
    label: job.title,
  }));

  const handleFormSubmit = (data: FormSchema) => {
    mixpanelUtils.trackFormSubmission("Job selection submit", MIXPANEL_SCREENS.ORPHANED_WORKABLE_JOBS);
    handleSubmit(data.jobId, data.companyId);
    form.reset();
    onOpenChange(false);
  };

  const render = () => {
    return (
      <Dialog
        onOpenChange={(open) => {
          form.reset();
          onOpenChange(open);
        }}
        open={open}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-slate-700">
              Select Job
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex min-w-0 flex-col gap-4 overflow-hidden"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <FormEnumSelect
                fieldName="companyId"
                form={form}
                label="Select a company"
                onChange={() => {
                  form.setValue("jobId", "");
                }}
                options={companyOptions}
                placeholder="Select a company"
              />
              <FormEnumSelect
                fieldName="jobId"
                form={form}
                label="Select a job"
                options={jobOptions}
                placeholder="Select a job"
              />
              <Column className="items-end gap-2">
                <Button
                  className="w-16 rounded-full"
                  disabled={isSubmitPending}
                  type="submit"
                >
                  {isSubmitPending ? (
                    <LoadingIndicator gearColor="white" variant="small" />
                  ) : (
                    <span>Submit</span>
                  )}
                </Button>
                {isSubmitError && (
                  <ErrorText errorMessage="An error occurred while linking the job. Please try again." />
                )}
              </Column>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
